import React, { useEffect, useState } from "react";
import { useUserContext } from "../config/UserContext";
import { useKeycloak } from "../config/KeycloakContext";
import type { Document } from "../../../shared/types/models/document";
import { DOCUMENT_TYPE } from "../../../shared/types/models/document";
import { useNavigate, useParams } from "react-router-dom";
import AdvancedGrantorCard from './AdvancedGrantorCard';

const makeCaseInsensitive = (obj: any) => {
  if (!obj || typeof obj !== 'object') return obj;
  const newObj = { ...obj };
  for (const key of Object.keys(obj)) {
    newObj[key.toLowerCase()] = obj[key];
  }
  return newObj;
};

const collectSchemaFields = (obj: any, out: any[] = []): any[] => {
  if (!obj || typeof obj !== 'object') return out;
  if (Array.isArray(obj)) {
    obj.forEach(item => collectSchemaFields(item, out));
    return out;
  }
  if (obj.key && (obj.displayRole || obj.label)) {
    out.push(obj);
  }
  Object.values(obj).forEach(val => collectSchemaFields(val, out));
  return out;
};

const getValuesByRole = (ri: any, fields: any[], role: string, forceLabel: boolean = false) => {
  if (!fields || !ri) return null;
  const seenKeys = new Set<string>();
  const matchingFields = fields.filter(f => {
    if (f?.displayRole !== role || !f?.key) return false;
    const keyLower = String(f.key).toLowerCase();
    if (seenKeys.has(keyLower)) return false;
    seenKeys.add(keyLower);
    return true;
  });
  if (matchingFields.length === 0) return null;
  
  const values = matchingFields.map(f => {
    const val = ri[f.key.toLowerCase()];
    if (!val) return null;
    return (forceLabel || matchingFields.length > 1) ? `${f.label}: ${val}` : val;
  }).filter(Boolean);
  
  return values.length > 0 ? values.join(' | ') : null;
};

const SummaryCard: React.FC<{
  config?: any;
  nextStepLabel?: string;
  onGenerateDocument?: () => void;
  onPayNow?: () => void;
  onBack?: () => void;
  generating?: boolean;
  paying?: boolean;
  onDownloadDocument?: () => void;
  generated?: boolean;
  viewMode?: boolean;
}> = ({ config, nextStepLabel, onGenerateDocument, onPayNow, onBack, generating, paying, onDownloadDocument, generated, viewMode = false }) => {
  const { document, freeze, parcels, state, county, entities, grantors, apiBaseUrl } = useUserContext();
  const { getToken } = useKeycloak();
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();

  const [releases, setReleases] = useState<Document[]>([]);
  const [loadingReleases, setLoadingReleases] = useState(false);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const [frozenDeedIndex, setFrozenDeedIndex] = useState("");
  const [frozenDeedFiledDate, setFrozenDeedFiledDate] = useState("");
  const [savingRecordingInfo, setSavingRecordingInfo] = useState(false);
  const [recordingInfoSaved, setRecordingInfoSaved] = useState(false);

  // Fetch Releases and Document Attributes (for viewMode)
  useEffect(() => {
    if (!document?.lookup || document.type !== DOCUMENT_TYPE.LIEN) return;
    let isMounted = true;
    
    // Fetch Releases
    if (viewMode) {
      setLoadingReleases(true);
      (async () => {
        try {
          const token = await getToken();
          if (!token) return;
          const res = await fetch(`${apiBaseUrl}/api/frozendeed/documents?freezeLookup=${freeze?.lookup}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!res.ok) throw new Error('Failed to fetch releases');
          const data = await res.json();
          if (isMounted) {
            const docs = Array.isArray(data.data) ? data.data : [];
            const relatedReleases = docs.filter((d: any) => 
              d.type === DOCUMENT_TYPE.LIEN_RELEASE && 
              (d.parentReference && d.parentReference === document.frozenDeedReference)
            );
            setReleases(relatedReleases);
          }
        } catch (err) {
          console.error(err);
        } finally {
          if (isMounted) setLoadingReleases(false);
        }
      })();
    }

    // Fetch existing attributes
    if (viewMode) {
      (async () => {
        try {
          const token = await getToken();
          if (!token) return;
          const res = await fetch(`${apiBaseUrl}/api/documents/${document.lookup}/attributes`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            if (isMounted && data.data) {
              const idxAttr = data.data.find((a: any) => a.key === 'frozenDeedIndex');
              const dateAttr = data.data.find((a: any) => a.key === 'frozenDeedFiledDate');
              if (idxAttr) setFrozenDeedIndex(idxAttr.value || "");
              if (dateAttr) setFrozenDeedFiledDate(dateAttr.value || "");
              if (idxAttr || dateAttr) setRecordingInfoSaved(true);
            }
          }
        } catch (err) {
          console.error('Failed to fetch attributes', err);
        }
      })();
    }
    return () => { isMounted = false; };
  }, [document?.id, document?.type, document?.frozenDeedReference, freeze?.lookup, apiBaseUrl, getToken, viewMode]);

  const handleSaveRecordingInfo = async () => {
    if (!document) return;
    setSavingRecordingInfo(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      // Save index
      await fetch(`${apiBaseUrl}/api/documents/${document.lookup}/attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ key: 'frozenDeedIndex', value: frozenDeedIndex })
      });
      // Save date
      await fetch(`${apiBaseUrl}/api/documents/${document.lookup}/attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ key: 'frozenDeedFiledDate', value: frozenDeedFiledDate })
      });
      // Also save to freeze table for amendment/release reference
      if (freeze?.lookup) {
        await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${freeze.lookup}/filed-info`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ countyIndex: frozenDeedIndex, filedDate: frozenDeedFiledDate })
        });
      }
      setRecordingInfoSaved(true);
    } catch (err) {
      console.error(err);
      alert('Failed to save recording info');
    } finally {
      setSavingRecordingInfo(false);
    }
  };

  const handleCreateRelease = async () => {
    if (!document) return;
    setReleaseLoading(true);
    setReleaseError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      
      const res = await fetch(`${apiBaseUrl}/api/frozendeed/document/clone/${document.lookup}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({}) // clone.ts will inherit attributes from the parent now
      });
      const data = await res.json();
      if (data && data.success && data.data && (data.data.documentId || data.data.lookup)) {
        const docId = data.data.documentId || data.data.lookup;
        // Navigate to the unified flow — FlowPage handles schema resolution
        navigate(`/document/${docId}/user-agreement`);
      } else {
        throw new Error(data.error || 'Unexpected response from server');
      }
    } catch (err: any) {
      setReleaseError(err.message || 'Failed to create release');
    } finally {
      setReleaseLoading(false);
    }
  };

  const handleDownloadInternal = async () => {
    if (onDownloadDocument) {
      onDownloadDocument();
      return;
    }
    // Fallback for ViewMode if it doesn't get passed as prop
    if (!document?.lookup || !apiBaseUrl) return;
    try {
      const token = getToken ? await getToken() : null;
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${apiBaseUrl}/api/frozendeed/document/${document.lookup}/generated`, {
        method: 'GET',
        headers,
      });
      if (!response.ok) throw new Error('Failed to download document');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `document-${document.lookup}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }, 100);
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Error downloading document');
    }
  };

  const handlePrintInternal = async () => {
    if (!document?.lookup || !apiBaseUrl) return;
    try {
      const token = getToken ? await getToken() : null;
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(`${apiBaseUrl}/api/frozendeed/document/${document.lookup}/generated`, {
        method: 'GET',
        headers,
      });
      if (!response.ok) throw new Error('Failed to fetch document for printing');
      const blob = await response.blob();
      // Explicitly define as application/pdf so browsers open their native PDF viewer
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error preparing document for print:', err);
      alert('Error preparing document for print');
    }
  };

  const handleBack = () => {
    if (onBack && !viewMode) {
      onBack();
      return;
    }
    if (documentId && !viewMode) {
      navigate(`/document/${documentId}/biometrics`);
    } else {
      window.history.back();
    }
  };

  let titleText = 'Unknown Document';
  if (document?.type === DOCUMENT_TYPE.LIEN) {
    if (viewMode) {
      titleText = `Frozen Deed Number ${document?.frozenDeedReference ? `(#${document.frozenDeedReference})` : ''}`;
    } else {
      titleText = document?.generated ? 'Frozen Deed Generated' : 'Review Frozen Deed';
    }
  } else if (document?.type === DOCUMENT_TYPE.LIEN_RELEASE) {
    titleText = 'Lien Release Document';
  } else if (document?.type === DOCUMENT_TYPE.AMENDMENT) {
    titleText = 'Amendment Document';
  }

  return (
    <div className="w-full flex flex-col gap-8">
      {/* Title */}
      <div>
        <div className="text-[24px] font-plus-jakarta font-semibold text-[#141435] mb-2">
          {titleText}
        </div>
        {!viewMode && !document?.generated && (
          <div className="text-[15px] font-plus-jakarta text-[#8A6D1B] bg-[#FFFBEA] border border-[#FFD600] rounded-md p-3">
            <strong>Please check for accuracy!</strong> Data cannot be changed once the document is generated.
          </div>
        )}
      </div>

      {/* Property Info */}
      <div>
        <div className="text-[18px] font-plus-jakarta font-semibold text-[#141435] mb-2">Property Info</div>
        <div className="flex flex-col gap-4 text-[16px]">
          {(() => {
            const deedsList: any[] = Array.isArray(freeze?.countyIndexes) ? freeze.countyIndexes : [];
            const realParcels: any[] = (parcels || []).filter((p: any) => !p?.virtual);

            if (deedsList.length === 0 && realParcels.length === 0) {
              return <div className="text-[#485D7D]">No property information added.</div>;
            }

            const parsedDocSchema = typeof county?.documentSchema === 'string'
              ? (() => { try { return JSON.parse(county.documentSchema); } catch { return null; } })()
              : county?.documentSchema;
            const parsedSimpleSchema = typeof (county as any)?.simpleDocumentSchema === 'string'
              ? (() => { try { return JSON.parse((county as any).simpleDocumentSchema); } catch { return null; } })()
              : (county as any)?.simpleDocumentSchema;

            const allFields = [
              ...(config?.instrument?.fields || []),
              ...(config?.parcel?.fields || []),
              ...collectSchemaFields(parsedDocSchema),
              ...collectSchemaFields(parsedSimpleSchema),
            ];

            const formatRecordAddress = (rec: any, recRi: any) => {
              const primaryStreet = rec?.address || rec?.street1 || recRi?.address || recRi?.propertyaddress || '';
              const c = rec?.city || '';
              const s = state?.abbr || '';
              const z = rec?.zipcode || '';
              let cityStateZip = '';
              if (c || z) {
                const hasState = Boolean(s && (c.includes(` ${s} `) || c.endsWith(` ${s}`) || c.includes(`, ${s}`) || c.includes(`,${s}`)));
                cityStateZip = `${c}${c && s && !hasState ? ', ' + s : ''} ${z}`.trim();
              }
              const showCityStateZip = Boolean(cityStateZip && (!primaryStreet || !primaryStreet.includes(cityStateZip)));
              const street2Val = rec?.street2 || '';
              const hasAddress = Boolean(primaryStreet || (street2Val && street2Val.trim() !== '') || showCityStateZip);
              return { primaryStreet, street2Val, cityStateZip, showCityStateZip, hasAddress };
            };

            const renderParcelCard = (p: any, tractIndex: number, totalParcels: number) => {
              let parcelRiRaw: any = {};
              if (typeof p.recordingIndexes === 'string') {
                try { parcelRiRaw = JSON.parse(p.recordingIndexes); } catch {}
              } else {
                parcelRiRaw = p.recordingIndexes || {};
              }
              const parcelRi = makeCaseInsensitive(parcelRiRaw);
              const roleParcelNum = getValuesByRole(parcelRi, allFields, 'parcel-number');
              const pNum = p.parcelNumber || roleParcelNum || parcelRi.pid || parcelRi.apn || parcelRi.parcelnum || parcelRi.pin || parcelRi.parcelid || parcelRi.upi || parcelRi.parcelnumber || null;
              const roleLegal = getValuesByRole(parcelRi, allFields, 'legal-description');
              const pLegal = p.legalDescription || p.abbreviatedLegalDescription || roleLegal || parcelRi.legaldescription || parcelRi.legal || parcelRi.legal_description || parcelRi.brieflegal || null;
              const addr = formatRecordAddress(p, parcelRi);

              return (
                <div key={p.id || `parcel-${tractIndex}`} className="bg-white border rounded-lg p-4 shadow-sm flex flex-col gap-1">
                  {totalParcels > 1 && (
                    <div className="font-bold text-[#141435] mb-1">Tract {tractIndex + 1}</div>
                  )}
                  {pNum && pNum !== 'N/A' && (
                    <div className="flex flex-row gap-2 items-center mb-1">
                      <span className="text-[#485D7D] text-[15px] font-plus-jakarta">Parcel Number:</span>
                      <span className="text-[#141435] text-[15px] font-plus-jakarta font-semibold">
                        {pNum}
                      </span>
                    </div>
                  )}
                  {addr.hasAddress && (
                    <div className="flex flex-row gap-2 items-baseline mb-2">
                      <span className="text-[#485D7D] text-[15px] font-plus-jakarta shrink-0">Address:</span>
                      <div className="text-[#141435] text-[15px] font-plus-jakarta font-semibold">
                        {addr.primaryStreet && <div>{addr.primaryStreet}</div>}
                        {addr.street2Val && addr.street2Val.trim() !== '' && <div>{addr.street2Val}</div>}
                        {addr.showCityStateZip && <div>{addr.cityStateZip}</div>}
                      </div>
                    </div>
                  )}
                  {pLegal && (
                    <div className="pt-3 border-t border-gray-100 flex flex-col gap-1">
                      <span className="text-[#485D7D] text-[13px] font-bold tracking-wider uppercase">Legal Description</span>
                      <div className="text-[#141435] text-[14px] leading-relaxed">
                        {pLegal}
                      </div>
                    </div>
                  )}
                </div>
              );
            };

            let globalTractCounter = 0;
            const claimedParcelIds = new Set<any>();

            return (
              <>
                {deedsList.map((deed: any, deedIdx: number) => {
                  let deedRiRaw: any = {};
                  if (typeof deed.recordingIndexes === 'string') {
                    try { deedRiRaw = JSON.parse(deed.recordingIndexes); } catch {}
                  } else {
                    deedRiRaw = deed.recordingIndexes || {};
                  }
                  const deedRi = makeCaseInsensitive(deedRiRaw);
                  const roleRef =
                    getValuesByRole(deedRi, allFields, 'deed-PrimaryIndex', true) ||
                    getValuesByRole(deedRi, allFields, 'reference', true);

                  const rawEntries = Object.entries(deedRiRaw).filter(([k, v]) => k !== 'id' && v !== undefined && v !== null && String(v).trim() !== '');
                  const deedHeaderText = roleRef || (rawEntries.length > 0
                    ? rawEntries.map(([k, v]) => {
                        const fieldDef = allFields.find((f: any) => String(f.key).toLowerCase() === k.toLowerCase());
                        return `${fieldDef?.label || k}: ${v}`;
                      }).join(' | ')
                    : `Reference #${deedIdx + 1}`);

                  const deedAddr = formatRecordAddress(deed, deedRi);
                  const deedLegal = deed.legalDescription || deed.abbreviatedLegalDescription || deedRi.legaldescription || deedRi.legal || deedRi.legal_description || null;
                  const deedParcels = realParcels.filter(
                    (p: any) => String(p.countyIndexId) === String(deed.id) || (deedsList.length === 1 && !p.countyIndexId)
                  );
                  deedParcels.forEach((p: any) => { if (p.id !== undefined && p.id !== null) claimedParcelIds.add(p.id); });

                  const hasDeedBody = deedAddr.hasAddress || Boolean(deedLegal) || deedParcels.length === 0;

                  return (
                    <div key={deed.id || `deed-${deedIdx}`} className="flex flex-col gap-3">
                      {hasDeedBody ? (
                        <div className="bg-white border rounded-lg p-4 shadow-sm flex flex-col gap-2">
                          <div className="font-bold text-[#141435] text-lg border-b pb-2">{deedHeaderText}</div>
                          {deedAddr.hasAddress && (
                            <div className="flex flex-row gap-2 items-baseline">
                              <span className="text-[#485D7D] text-[15px] font-plus-jakarta shrink-0">Address:</span>
                              <div className="text-[#141435] text-[15px] font-plus-jakarta font-semibold">
                                {deedAddr.primaryStreet && <div>{deedAddr.primaryStreet}</div>}
                                {deedAddr.street2Val && deedAddr.street2Val.trim() !== '' && <div>{deedAddr.street2Val}</div>}
                                {deedAddr.showCityStateZip && <div>{deedAddr.cityStateZip}</div>}
                              </div>
                            </div>
                          )}
                          {(deedLegal || deedParcels.length === 0) && (
                            <div className="pt-2 border-t border-gray-100 flex flex-col gap-1">
                              <span className="text-[#485D7D] text-[13px] font-bold tracking-wider uppercase">Legal Description</span>
                              <div className="text-[#141435] text-[14px] leading-relaxed">
                                {deedLegal || <span className="italic text-[#9EAABE]">No legal description provided.</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="font-bold text-[#141435] text-lg border-b pb-1 mt-2">{deedHeaderText}</div>
                      )}
                      {deedParcels.map((p: any) => renderParcelCard(p, globalTractCounter++, realParcels.length))}
                    </div>
                  );
                })}
                {realParcels
                  .filter((p: any) => !(p.id !== undefined && p.id !== null && claimedParcelIds.has(p.id)) && deedsList.length === 0)
                  .map((p: any) => renderParcelCard(p, globalTractCounter++, realParcels.length))}
              </>
            );
          })()}
        </div>
      </div>

      {/* Grantors */}
      <div>
        <div className="text-[18px] font-plus-jakarta font-semibold text-[#141435] mb-2">Grantors</div>
        <div className="flex flex-col gap-3">
          {grantors.length === 0 && <div className="text-[#485D7D]">No grantors added.</div>}
          {grantors.length > 0 && (
            <AdvancedGrantorCard
              onBack={() => {}}
              onContinue={() => {}}
              readOnly={true}
            />
          )}
        </div>
      </div>

      {/* View Mode Specifics: Recording Info & Lien Releases */}
      {viewMode && document?.type === DOCUMENT_TYPE.LIEN && (
        <>
          {/* Recording Info */}
          <div>
            <div className="text-[18px] font-plus-jakarta font-semibold text-[#141435] mb-2">Recording Information</div>
            <div className="bg-white border rounded-lg p-4 shadow-sm">
              <p className="text-sm text-[#485D7D] mb-4">
                Enter the recording details assigned by the county after this Frozen Deed was filed. This information is required before a Lien Release can be created.
              </p>
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta mb-1 block">Frozen Deed Index (Book/Page or Auditor No.)</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 rounded border border-[#D7DFEC] bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none"
                    value={frozenDeedIndex}
                    onChange={e => setFrozenDeedIndex(e.target.value)}
                    placeholder="e.g. 1234 5678"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta mb-1 block">Date Filed</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 rounded border border-[#D7DFEC] bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none"
                    value={frozenDeedFiledDate}
                    onChange={e => setFrozenDeedFiledDate(e.target.value)}
                  />
                </div>
              </div>
              <button
                className="bg-[#0B4BB6] text-white font-semibold text-[14px] py-2 px-6 rounded transition-colors disabled:opacity-50"
                onClick={handleSaveRecordingInfo}
                disabled={!frozenDeedIndex || !frozenDeedFiledDate || savingRecordingInfo}
              >
                {savingRecordingInfo ? 'Saving...' : recordingInfoSaved ? 'Update Recording Info' : 'Save Recording Info'}
              </button>
            </div>
          </div>

          {/* Lien Releases */}
          <div>
            <div className="text-[18px] font-plus-jakarta font-semibold text-[#141435] mb-2">Lien Releases</div>
            {loadingReleases ? (
              <div className="text-[#485D7D]">Loading releases...</div>
            ) : releases.length > 0 ? (
              <div className="flex flex-col gap-3">
                {releases.map(release => (
                  <div key={release.id} className="bg-white border rounded-lg p-4 flex justify-between items-center shadow-sm">
                    <div>
                      <div className="font-semibold text-[15px] text-[#141435]">Lien Release</div>
                      <div className="text-[14px] text-[#485D7D]">
                        {release.generated ? 'Generated' : 'Draft'}
                      </div>
                    </div>
                    <button
                      className="text-[#0B4BB6] font-semibold text-[14px] hover:underline"
                      onClick={() => {
                        if (release.generated) {
                          navigate(`/document/${release.lookup}/details`);
                        } else {
                          // Navigate to /start — FlowPage will auto-route
                          navigate(`/document/${release.lookup}/start`);
                        }
                      }}
                    >
                      {release.generated ? 'View Release' : 'Resume Draft'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#F9FAFB] rounded-lg p-5 text-[#485D7D] text-[15px] font-plus-jakarta flex flex-col sm:flex-row justify-between items-center gap-4">
                <span className="italic text-[#9EAABE]">No releases have been created for this lien.</span>
                <button
                  className="bg-[#0B4BB6] text-white font-semibold text-[14px] py-2 px-6 rounded transition-colors disabled:opacity-50 whitespace-nowrap"
                  onClick={handleCreateRelease}
                  disabled={!recordingInfoSaved || releaseLoading}
                  title={!recordingInfoSaved ? "Please save recording information above first" : ""}
                >
                  {releaseLoading ? 'Creating...' : 'Create Release'}
                </button>
              </div>
            )}
            {releaseError && <div className="text-red-500 text-sm mt-2">{releaseError}</div>}
          </div>
        </>
      )}

      {/* Action Bar */}
      <div className="flex flex-row w-full max-w-[700px] gap-4 mt-6 mx-auto">
        {!viewMode && !document?.generated && (
          <button
            type="button"
            className="flex-1 bg-[#7C8B9D] text-white font-plus-jakarta font-bold text-[15px] py-[10px] px-5 rounded-full hover:bg-[#485D7D] transition-colors"
            style={{ borderRadius: 60 }}
            onClick={handleBack}
          >
            Back
          </button>
        )}
        
        {document?.generated === true || viewMode ? (
          <>
            <button
              className="flex-1 bg-white border-2 border-[#0B4BB6] text-[#0B4BB6] font-plus-jakarta font-bold text-[15px] py-2 px-8 rounded-full hover:bg-[#F9FAFB] transition-colors"
              style={{ borderRadius: 60 }}
              onClick={handlePrintInternal}
            >
              Print Document
            </button>
            <button
              className="flex-1 bg-[#0B4BB6] border-2 border-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-2 px-8 rounded-full hover:bg-[#23235a] transition-colors"
              style={{ borderRadius: 60 }}
              onClick={handleDownloadInternal}
            >
              Download Document
            </button>
          </>
        ) : (
          <>
            {(document?.paid === false || document?.paid === undefined) && (
              <button
                className="flex-1 bg-[#0B4BB6] border-2 border-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-2 px-8 rounded-full hover:bg-[#23235a] transition-colors"
                style={{ borderRadius: 60 }}
                onClick={onPayNow}
                disabled={paying}
              >
                {paying ? 'Processing Payment...' : 'Pay Now'}
              </button>
            )}
            {document?.paid === true && (
              <button
                className="flex-1 bg-[#0B4BB6] border-2 border-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-2 px-8 rounded-full hover:bg-[#23235a] transition-colors"
                style={{ borderRadius: 60 }}
                onClick={onGenerateDocument}
                disabled={generating}
              >
                {generating ? 'Generating...' : (nextStepLabel || 'Generate Document')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SummaryCard;